import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  message,
  Row,
  Col
} from 'antd';
import apiService from "services/api/apiService";

const { TextArea } = Input;
const { Option } = Select;

interface TrainingProgramFormProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  editingProgram?: any;
}

const TrainingProgramForm: React.FC<TrainingProgramFormProps> = ({
  visible,
  onCancel,
  onSuccess,
  editingProgram
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible && editingProgram) {
      // Map the API data to form fields
      form.setFieldsValue({
        program_id: editingProgram.program_id,
        title: editingProgram.title,
        description: editingProgram.description,
        category: editingProgram.category,
        type: editingProgram.type,
        duration_hours: editingProgram.duration_hours,
        max_participants: editingProgram.max_participants,
        cost_per_participant: editingProgram.cost_per_participant,
        trainer_name: editingProgram.trainer_name,
        trainer_email: editingProgram.trainer_email,
        materials_url: editingProgram.materials_url,
        prerequisites: editingProgram.prerequisites,
        learning_objectives: editingProgram.learning_objectives,
        status: editingProgram.status
      });
    } else if (visible) {
      form.resetFields();
      form.setFieldsValue({
        program_id: `TRN${Date.now()}`,
        category: 'technical',
        type: 'online',
        status: 'draft',
        duration_hours: 8,
        max_participants: 20,
        cost_per_participant: 0
      });
    }
  }, [visible, editingProgram, form]);

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      
      // Prepare the data according to your database schema
      const formData = {
        program_id: values.program_id,
        title: values.title,
        description: values.description,
        category: values.category,
        type: values.type,
        duration_hours: values.duration_hours,
        max_participants: values.max_participants,
        cost_per_participant: values.cost_per_participant || 0,
        trainer_name: values.trainer_name,
        trainer_email: values.trainer_email,
        materials_url: values.materials_url || '',
        prerequisites: values.prerequisites || '',
        learning_objectives: values.learning_objectives || '',
        status: values.status
      };

      let response;
      if (editingProgram) {
        response = await apiService.updateTrainingProgram(editingProgram.id, formData);
      } else {
        response = await apiService.createTrainingProgram(formData);
      }

      if (response.success) {
        message.success(editingProgram ? 'Training program updated successfully' : 'Training program created successfully');
        form.resetFields();
        onSuccess();
      } else {
        message.error(response.error || 'Operation failed');
      }
    } catch (error) {
      console.error('Form submit error:', error);
      message.error('An error occurred while saving the program');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={editingProgram ? 'Edit Training Program' : 'Create Training Program'}
      open={visible}
      onCancel={onCancel}
      onOk={() => form.submit()}
      confirmLoading={loading}
      width={900}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="program_id"
              label="Program ID"
              rules={[{ required: true, message: 'Please enter program ID' }]}
            >
              <Input placeholder="Enter unique program ID" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="title"
              label="Program Title"
              rules={[{ required: true, message: 'Please enter program title' }]}
            >
              <Input placeholder="Enter program title" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="description"
          label="Description"
          rules={[{ required: true, message: 'Please enter description' }]}
        >
          <TextArea rows={3} placeholder="Enter program description" />
        </Form.Item>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="category"
              label="Category"
              rules={[{ required: true, message: 'Please select category' }]}
            >
              <Select placeholder="Select category">
                <Option value="technical">Technical</Option>
                <Option value="soft_skills">Soft Skills</Option>
                <Option value="compliance">Compliance</Option>
                <Option value="leadership">Leadership</Option>
                <Option value="safety">Safety</Option>
                <Option value="orientation">Orientation</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="type"
              label="Training Type"
              rules={[{ required: true, message: 'Please select type' }]}
            >
              <Select placeholder="Select type">
                <Option value="online">Online</Option>
                <Option value="classroom">Classroom</Option>
                <Option value="workshop">Workshop</Option>
                <Option value="seminar">Seminar</Option>
                <Option value="certification">Certification</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="duration_hours"
              label="Duration (Hours)"
              rules={[{ required: true, message: 'Please enter duration' }]}
            >
              <InputNumber min={0.5} max={999} step={0.5} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="max_participants"
              label="Max Participants"
              rules={[{ required: true, message: 'Please enter max participants' }]}
            >
              <InputNumber min={1} max={1000} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="cost_per_participant"
              label="Cost per Participant (₦)"
            >
              <InputNumber min={0} step={100} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="status"
              label="Status"
              rules={[{ required: true, message: 'Please select status' }]}
            >
              <Select>
                <Option value="draft">Draft</Option>
                <Option value="active">Active</Option>
                <Option value="completed">Completed</Option>
                <Option value="cancelled">Cancelled</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="trainer_name"
              label="Trainer Name"
              rules={[{ required: true, message: 'Please enter trainer name' }]}
            >
              <Input placeholder="Enter trainer name" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="trainer_email"
              label="Trainer Email"
              rules={[
                { required: true, message: 'Please enter trainer email' },
                { type: 'email', message: 'Please enter valid email' }
              ]}
            >
              <Input placeholder="Enter trainer email" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="materials_url"
          label="Training Materials URL"
        >
          <Input placeholder="Enter URL for training materials (optional)" />
        </Form.Item>

        <Form.Item
          name="prerequisites"
          label="Prerequisites"
        >
          <TextArea rows={2} placeholder="Enter prerequisites (optional)" />
        </Form.Item>

        <Form.Item
          name="learning_objectives"
          label="Learning Objectives"
        >
          <TextArea rows={2} placeholder="Enter learning objectives (optional)" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default TrainingProgramForm;
